const isGeoJsonObject = (value) =>
  value &&
  typeof value === 'object' &&
  typeof value.type === 'string' &&
  ['Feature', 'FeatureCollection', 'Polygon', 'MultiPolygon', 'GeometryCollection'].includes(
    value.type
  )

const ringToGeoJsonPolygon = (ring) => {
  if (!Array.isArray(ring) || ring.length < 3) return null

  const coordinates = ring.map((point) => {
    if (Array.isArray(point) && point.length >= 2) {
      return [Number(point[0]), Number(point[1])]
    }
    if (point && typeof point === 'object') {
      const lng = Number(point.lng ?? point.lon ?? point.longitude)
      const lat = Number(point.lat ?? point.latitude)
      return [lng, lat]
    }
    return null
  })

  if (coordinates.some((point) => !point || point.some((n) => !Number.isFinite(n)))) {
    return null
  }

  const first = coordinates[0]
  const last = coordinates[coordinates.length - 1]
  if (first[0] !== last[0] || first[1] !== last[1]) {
    coordinates.push([...first])
  }

  return {
    type: 'Feature',
    properties: { source: 'ring' },
    geometry: {
      type: 'Polygon',
      coordinates: [coordinates],
    },
  }
}

const normalizeBoundaryValue = (value) => {
  if (!value) return null

  if (typeof value === 'string') {
    try {
      return normalizeBoundaryValue(JSON.parse(value))
    } catch {
      return null
    }
  }

  if (isGeoJsonObject(value)) {
    return value
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return null

    if (Array.isArray(value[0]) && Array.isArray(value[0][0])) {
      return {
        type: 'Feature',
        properties: { source: 'coordinates' },
        geometry: {
          type: 'Polygon',
          coordinates: value,
        },
      }
    }

    return ringToGeoJsonPolygon(value)
  }

  if (value.type === 'polygon' && Array.isArray(value.coordinates)) {
    return normalizeBoundaryValue(value.coordinates)
  }

  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Array.isArray(value.coordinates) &&
    !value.type
  ) {
    return normalizeBoundaryValue({
      type: 'Polygon',
      coordinates: value.coordinates,
    })
  }

  if (value.geojson) {
    return normalizeBoundaryValue(value.geojson)
  }

  if (value.geometry) {
    return normalizeBoundaryValue(value.geometry)
  }

  return null
}

const BOUNDARY_FIELD_NAMES = [
  'boundary_polygon',
  'boundaryPolygon',
  'boundaryGeoJson',
  'boundary_geojson',
  'cityLimits',
  'city_limits',
  'cityLimitsGeoJson',
  'city_limits_geojson',
  'boundary',
  'jurisdictionBoundary',
  'jurisdiction_boundary',
  'limitsPolygon',
  'limits_polygon',
]

export const parseBoundaryGeoJson = (source) => {
  if (!source || typeof source !== 'object') return null

  for (const field of BOUNDARY_FIELD_NAMES) {
    const normalized = normalizeBoundaryValue(source[field])
    if (normalized) return normalized
  }

  const nestedAuthority = source?.authority
  if (nestedAuthority && nestedAuthority !== source) {
    const fromAuthority = parseBoundaryGeoJson(nestedAuthority)
    if (fromAuthority) return fromAuthority
  }

  const nestedCrawl = source?.crawler_validation?.crawl ?? source?.crawl
  if (nestedCrawl && nestedCrawl !== source) {
    return parseBoundaryGeoJson(nestedCrawl)
  }

  return null
}

export const hasBoundaryGeometry = (boundaryGeoJson) => Boolean(parseBoundaryGeoJson(boundaryGeoJson))

const visitCoordinates = (coordinates, onPoint) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return

  if (typeof coordinates[0] === 'number') {
    onPoint(Number(coordinates[0]), Number(coordinates[1]))
    return
  }

  coordinates.forEach((part) => visitCoordinates(part, onPoint))
}

/** Approximate center from GeoJSON bounds (for map links and fallbacks). */
export const getGeoJsonCenter = (geoJson) => {
  if (!geoJson) return null

  let minLng = Infinity
  let maxLng = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity
  let pointCount = 0

  const addPoint = (lng, lat) => {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    pointCount += 1
  }

  const walkGeometry = (geometry) => {
    if (!geometry) return
    if (geometry.type === 'GeometryCollection') {
      geometry.geometries?.forEach(walkGeometry)
      return
    }
    visitCoordinates(geometry.coordinates, addPoint)
  }

  const walkGeoJson = (value) => {
    if (!value) return
    if (value.type === 'Feature') {
      walkGeometry(value.geometry)
      return
    }
    if (value.type === 'FeatureCollection') {
      value.features?.forEach(walkGeoJson)
      return
    }
    walkGeometry(value)
  }

  walkGeoJson(geoJson)
  if (pointCount === 0) return null

  return {
    latitude: (minLat + maxLat) / 2,
    longitude: (minLng + maxLng) / 2,
  }
}
